// src/utils/cacheDevTools.ts
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import type { AxiosInstance, AxiosResponse } from 'axios';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';

export interface CacheKeyEntry {
  key: string;
  pattern: string;
  endpoint: string;
}

export interface ResolvedUser {
  user_id: number;
  username: string;
  display_name: string;
  email: string;
}

export interface CacheDevToolsState {
  isActive: boolean;
  entries: CacheKeyEntry[];
  resolvedUser: ResolvedUser | null;
  resolving: boolean;
  deleting: boolean;
  deleteKeys: (keys: string[]) => Promise<void>;
  deleteByUser: (patterns: string[], userId: number) => Promise<void>;
  clearPage: () => Promise<void>;
  resolveUser: (url: string) => Promise<void>;
  clearResolvedUser: () => void;
}

const CacheContext = createContext<CacheDevToolsState | null>(null);

export { CacheContext };

export function useCacheDevTools(): CacheDevToolsState {
  const ctx = useContext(CacheContext);
  if (!ctx) throw new Error('useCacheDevTools must be used inside CacheDevToolsProvider');
  return ctx;
}

function isShowCacheKeyActive(): boolean {
  return new URLSearchParams(window.location.search).get('show_cache_key') === '1';
}

const authHeader = (): Record<string, string> => {
  try {
    const stored = localStorage.getItem('authenticated_user');
    if (!stored) return {};
    const parsed = JSON.parse(stored) as Record<string, unknown>;
    const token = (parsed?.access_token || parsed?.token) as string | undefined;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
};

export function CacheDevToolsProvider({ children }: { children: React.ReactNode }) {
  const [entries, setEntries] = useState<CacheKeyEntry[]>([]);
  const [resolvedUser, setResolvedUser] = useState<ResolvedUser | null>(null);
  const [resolving, setResolving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isActive = isShowCacheKeyActive();

  const addEntries = useCallback((newEntries: CacheKeyEntry[]) => {
    setEntries(prev => {
      const existing = new Set(prev.map(e => e.key));
      const fresh = newEntries.filter(e => !existing.has(e.key));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  }, []);

  // Expose addEntries so initCacheDevTools can call it
  const addEntriesRef = useRef(addEntries);
  addEntriesRef.current = addEntries;
  (window as Window & { __cacheDevAddEntries?: (e: CacheKeyEntry[]) => void }).__cacheDevAddEntries = (e: CacheKeyEntry[]) => addEntriesRef.current(e);

  const deleteKeys = useCallback(async (keys: string[]) => {
    setDeleting(true);
    try {
      await axios.post(`${API_BASE}/api/admin/cache/delete/`, { keys }, { headers: authHeader() });
      setEntries(prev => prev.filter(e => !keys.includes(e.key)));
    } finally {
      setDeleting(false);
    }
  }, []);

  const deleteByUser = useCallback(async (patterns: string[], userId: number) => {
    setDeleting(true);
    try {
      await axios.post(
        `${API_BASE}/api/admin/cache/delete-by-user/`,
        { key_patterns: patterns, user_id: userId },
        { headers: authHeader() }
      );
    } finally {
      setDeleting(false);
    }
  }, []);

  const clearPage = useCallback(async () => {
    await deleteKeys(entries.map(e => e.key));
  }, [entries, deleteKeys]);

  const resolveUser = useCallback(async (url: string) => {
    setResolving(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/admin/cache/resolve-user/`,
        { url },
        { headers: authHeader() }
      );
      setResolvedUser(res.data as ResolvedUser);
    } catch {
      setResolvedUser(null);
    } finally {
      setResolving(false);
    }
  }, []);

  const clearResolvedUser = useCallback(() => setResolvedUser(null), []);

  return React.createElement(
    CacheContext.Provider,
    {
      value: {
        isActive,
        entries,
        resolvedUser,
        resolving,
        deleting,
        deleteKeys,
        deleteByUser,
        clearPage,
        resolveUser,
        clearResolvedUser,
      },
    },
    children
  );
}

export function initCacheDevTools(instances: AxiosInstance[]): void {
  if (!isShowCacheKeyActive()) return;

  instances.forEach(instance => {
    instance.interceptors.request.use(config => {
      config.headers.set('X-Show-Cache-Keys', '1');
      return config;
    });

    instance.interceptors.response.use((response: AxiosResponse) => {
      const rawHeader = response.headers['x-cache-keys'] as string | undefined;
      const patternHeader = response.headers['x-cache-key-patterns'] as string | undefined;
      if (rawHeader) {
        const rawKeys = rawHeader.split(',').filter(Boolean);
        const patterns = patternHeader ? patternHeader.split(',').filter(Boolean) : rawKeys;
        const endpoint = response.config.url || '';
        const newEntries: CacheKeyEntry[] = rawKeys.map((key, i) => ({
          key,
          pattern: patterns[i] || key,
          endpoint,
        }));
        const bridge = (window as Window & { __cacheDevAddEntries?: (e: CacheKeyEntry[]) => void }).__cacheDevAddEntries;
        if (bridge) {
          bridge(newEntries);
        }
      }
      return response;
    });
  });
}
